import { useEffect, useRef } from 'react';
import { prefetchDocumentThumbnails } from 'components/common/graphicUploader/documentThumbnailResolver';
import {
  clearDocumentPreviewCache,
  clearDocumentPreviewCachesForDocuments,
} from 'components/common/graphicUploader/previewUrlsCache';
import { DocumentsOnIssueView } from 'presentation/document/documentsOnIssueView';
import { pdfPreviewService } from 'services/fileService/pdfPreviewService';
import type { DocumentModel } from 'shared/domain/document/documentModel';
import { hasDocumentPreviewCacheRevisionChanged } from 'store/document/documentFileStateMutations';

/**
 * Keeps the previews of the issue's documents fresh: drops the cached preview of
 * a changed document, prefetches its thumbnail, and frees the previews once the
 * issue is left.
 */
export function useIssueDocumentPreviews(
  issueId: string | undefined,
  documents: DocumentModel[],
  documentsOnView: DocumentsOnIssueView
): void {
  const previousDocumentsRef = useRef<DocumentModel[]>([]);
  const documentsForCleanupRef = useRef<DocumentModel[]>([]);

  useEffect(() => {
    documentsForCleanupRef.current = documents;
  }, [documents]);

  useEffect(() => {
    previousDocumentsRef.current = [];
    return () => {
      pdfPreviewService.cancelInFlightRenders();
      clearDocumentPreviewCachesForDocuments(documentsForCleanupRef.current);
    };
  }, [issueId]);

  useEffect(() => {
    const previousByLocalId = new Map(
      previousDocumentsRef.current.map((doc) => [doc.localId, doc])
    );

    documents.forEach((document) => {
      const previous = previousByLocalId.get(document.localId);
      if (previous && hasDocumentPreviewCacheRevisionChanged(previous, document)) {
        clearDocumentPreviewCache({
          _id: document._id,
          localId: document.localId,
        });
      }
    });

    const allDocsOnView = [
      ...documentsOnView.issueDocuments,
      ...Object.values(documentsOnView.eventDocuments).flat(),
    ];

    const docsToPrefetch = allDocsOnView.filter((doc) => {
      if (typeof doc.localId !== 'number') {
        return false;
      }
      const previous = previousByLocalId.get(doc.localId);
      if (!previous) {
        // First sync for this issue: lazy load via listImage/miniature visibility.
        // Bulk prefetch here caused 10+ parallel getSources on every issue open.
        return false;
      }
      const current = documents.find((document) => document.localId === doc.localId);
      return Boolean(current && hasDocumentPreviewCacheRevisionChanged(previous, current));
    });

    previousDocumentsRef.current = documents;

    if (docsToPrefetch.length > 0) {
      prefetchDocumentThumbnails(docsToPrefetch);
    }
  }, [documents, documentsOnView]);
}
